import { ActionIcon, Center, Flex, Overlay } from '@mantine/core';
import { useClickOutside, useDisclosure, useHotkeys } from '@mantine/hooks';
import { IconHelpCircleFilled, IconX } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { i18nKeys, useTranslation } from 'locales';

interface AnalyticCardProps
  extends React.PropsWithChildren<{
    title: string | React.ReactNode;
    subtitle?: React.ReactNode;
    helpContent?: React.ReactNode;
    actions?: React.ReactNode;
    withBorder?: boolean;
    panelProps?: React.ComponentProps<typeof Panel>;
  }> {}

// Clears the tallest control a card puts beside its title (SegmentedControl size="xs",
// 30.6px), so a card with one and a card without keep level titles in the same grid row.
const HEADER_ROW_MIN_HEIGHT = 32;

export const AnalyticsCard = ({
  title,
  subtitle,
  helpContent,
  actions,
  children,
  withBorder = true,
  panelProps,
}: AnalyticCardProps) => {
  const { t } = useTranslation();
  const [isHelpOpen, helpHandlers] = useDisclosure();

  const ref = useClickOutside(helpHandlers.close);
  useHotkeys([['Escape', helpHandlers.close]]);

  return (
    <Panel
      style={{ overflow: 'visible' }}
      withBorder={withBorder}
      title={
        <Flex component="span" align="center" mih={HEADER_ROW_MIN_HEIGHT}>
          {title}
        </Flex>
      }
      subtitle={subtitle}
      topRight={actions}
      topLeft={
        helpContent != null && (
          <ActionIcon
            variant="transparent"
            color="purple.4"
            aria-label={t(i18nKeys.ANALYTICS.HELP_LABEL)}
            onClick={helpHandlers.open}
          >
            <IconHelpCircleFilled stroke={1.5} />
          </ActionIcon>
        )
      }
      {...panelProps}
    >
      {isHelpOpen && (
        <Overlay
          radius={16}
          ref={ref}
          p="xl"
          c="white"
          zIndex={10}
          backgroundOpacity={0.6}
          blur={7}
        >
          <ActionIcon
            pos="absolute"
            top={16}
            right={16}
            variant="transparent"
            color="white"
            onClick={helpHandlers.close}
          >
            <IconX stroke={1.5} />
          </ActionIcon>
          <Center>{helpContent}</Center>
        </Overlay>
      )}
      {children}
    </Panel>
  );
};
